import { useRef, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Play, Pause, Loader2, AlertCircle, RotateCw, Volume2, VolumeX, Sparkles, ShieldCheck } from 'lucide-react';
import { useLanguage } from '@/i18n/LanguageContext';
import { API_URL } from '@/utils/apiUrl';


const FALLBACK_SRC = '/videos/nexus-demo.mp4?v=3';

export default function VideoSection() {
  const { language } = useLanguage();
  const isTurkish = language === 'tr';
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [muted, setMuted] = useState(true);
  const [aiVideo, setAiVideo] = useState(null);   // { url, model, generated_at, ... } or null
  const [generating, setGenerating] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  // Check admin status from localStorage user object
  useEffect(() => {
    try {
      const u = JSON.parse(localStorage.getItem('user') || 'null');
      setIsAdmin(Boolean(u && u.is_admin));
    } catch (_) {
      setIsAdmin(false);
    }
  }, []);

  // Lazy-load AI-generated explainer if backend has one cached
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/explainer/latest`);
        if (!r.ok) return;
        const d = await r.json();
        if (alive && d.video?.url) {
          // Backend returns relative URL; resolve against API origin
          const fullUrl = d.video.url.startsWith('http')
            ? d.video.url
            : `${API_URL}${d.video.url}`;
          setAiVideo({ ...d.video, url: fullUrl });
        }
      } catch { /* silent */ }
    })();
    return () => { alive = false; };
  }, []);

  const generateAiVideo = async () => {
    if (!isAdmin) return;
    const token = (() => {
      try { return localStorage.getItem('session_token') || ''; } catch { return ''; }
    })();
    if (!token) {
      setError(isTurkish ? 'Admin oturumu gerekli' : 'Admin session required');
      return;
    }
    setGenerating(true);
    setError(null);
    try {
      const r = await fetch(`${API_URL}/api/explainer/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          language: isTurkish ? 'tr' : 'en',
          duration: 12,
          size: '1280x720',
          model: 'sora-2',
        }),
      });
      if (!r.ok) {
        const t = await r.text();
        throw new Error(t || `HTTP ${r.status}`);
      }
      const d = await r.json();
      const fullUrl = d.url.startsWith('http') ? d.url : `${API_URL}${d.url}`;
      setAiVideo({ ...d, url: fullUrl });
      // Force the video element to reload the new source
      const v = videoRef.current;
      if (v) { v.load(); }
    } catch (e) {
      setError(e?.message || (isTurkish ? 'Video üretilemedi' : 'Video generation failed'));
    } finally {
      setGenerating(false);
    }
  };

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onWaiting = () => setIsLoading(true);
    const onPlaying = () => { setIsLoading(false); setIsPlaying(true); setError(null); };
    const onCanPlay = () => setIsLoading(false);
    const onError = () => {
      const msg = v.error?.message || (isTurkish ? 'Video yüklenemedi' : 'Video failed to load');
      setError(msg);
      setIsLoading(false);
      setIsPlaying(false);
    };
    v.addEventListener('waiting', onWaiting);
    v.addEventListener('playing', onPlaying);
    v.addEventListener('canplay', onCanPlay);
    v.addEventListener('error', onError);
    return () => {
      v.removeEventListener('waiting', onWaiting);
      v.removeEventListener('playing', onPlaying);
      v.removeEventListener('canplay', onCanPlay);
      v.removeEventListener('error', onError);
    };
  }, [isTurkish]);

  const togglePlay = async () => {
    const v = videoRef.current;
    if (!v) return;
    setError(null);
    if (v.paused) {
      setIsLoading(true);
      try {
        v.muted = muted; // ensure muted state matches UI
        await v.play();
      } catch (e) {
        // Autoplay blocked — retry muted
        try {
          v.muted = true;
          setMuted(true);
          await v.play();
        } catch (e2) {
          setError(isTurkish
            ? 'Tarayıcınız videoyu oynatamadı. Lütfen sayfayı yenileyin veya başka bir tarayıcı deneyin.'
            : 'Your browser could not play the video. Please refresh or try another browser.');
          setIsLoading(false);
          setIsPlaying(false);
        }
      }
    } else {
      v.pause();
      setIsPlaying(false);
    }
  };

  const retry = () => {
    const v = videoRef.current;
    if (!v) return;
    setError(null);
    v.load();
    setTimeout(() => togglePlay(), 300);
  };

  const toggleMute = (e) => {
    e.stopPropagation();
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };

  return (
    <section
      id="video"
      className="relative py-24 md:py-32 bg-[#0A0D1A] overflow-hidden"
      data-testid="video-section"
    >
      {/* Ambient glows */}
      <div className="pointer-events-none absolute top-10 left-10 w-[500px] h-[500px] bg-[#4F9DFF] opacity-[0.06] blur-[140px] rounded-full" />
      <div className="pointer-events-none absolute bottom-10 right-10 w-[500px] h-[500px] bg-[#FF3B30] opacity-[0.05] blur-[140px] rounded-full" />

      <div className="relative z-10 max-w-6xl mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
          className="mb-12 max-w-2xl"
        >
          <span className="inline-block px-3 py-1.5 border border-white/10 text-xs font-mono uppercase tracking-wider text-white/60 mb-6">
            {isTurkish ? 'Platform Demosu' : 'Platform Demo'}
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-white leading-[1.1] mb-4">
            {isTurkish ? (
              <>
                Nexus Design AI'ı <span className="font-bold">iş başında</span> izleyin
              </>
            ) : (
              <>
                See Nexus Design AI <span className="font-bold">in action</span>
              </>
            )}
          </h2>
          <p className="text-white/60 max-w-xl leading-relaxed">
            {isTurkish
              ? 'Sesli komuttan üretim-yönelimli ön CAD taslağına uzanan iş akışı — işlem süresi proje karmaşıklığına, yapılandırılmış servislere ve doğrulama gereksinimlerine göre değişir.'
              : 'From voice command to a manufacturing-oriented preliminary CAD draft — processing time varies by project complexity, configured services and validation requirements.'}
          </p>
          <div className="mt-3">
            <span className="inline-block px-2 py-1 border border-amber-400/30 bg-amber-500/10 text-[10px] font-mono uppercase tracking-wider text-amber-300" data-testid="concept-video-tag">
              {isTurkish ? 'Konsept Vizyon Videosu — metrikler hedef niteliğindedir' : 'Concept Vision Video — metrics are targets'}
            </span>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="relative group"
        >
          {/* Frame */}
          <div className="relative bg-[#0A0A0A] border border-white/10 overflow-hidden">
            {/* Browser-chrome header */}
            <div className="flex items-center gap-2 px-4 py-3 border-b border-white/10 bg-[#121212]">
              <div className="flex gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full bg-[#FF5F56]" />
                <div className="w-2.5 h-2.5 rounded-full bg-[#FFBD2E]" />
                <div className="w-2.5 h-2.5 rounded-full bg-[#27C93F]" />
              </div>
              <span className="ml-2 text-xs text-white/60 font-mono">nexusdesignai.com/demo</span>
            </div>

            {/* Video */}
            <div className="relative aspect-video bg-black">
              <video
                ref={videoRef}
                key={aiVideo?.url || 'fallback'}
                className="w-full h-full object-cover"
                playsInline
                muted={muted}
                preload="auto"
                controls={isPlaying && !error}
                onPlay={() => { setIsPlaying(true); setError(null); }}
                onPause={() => setIsPlaying(false)}
                onEnded={() => setIsPlaying(false)}
                data-testid="demo-video"
                poster="/images/gallery/3d_modeling.webp"
              >
                {aiVideo?.url ? (
                  <source src={aiVideo.url} type="video/mp4" />
                ) : (
                  <>
                    {/* MP4 listed FIRST — Safari/iOS prefer it, and H.264 Main yuv420p works everywhere */}
                    <source src="/videos/nexus-demo.mp4?v=3" type="video/mp4" />
                    <source src="/videos/nexus-demo.webm?v=3" type="video/webm" />
                  </>
                )}
              </video>

              {/* AI-generated badge — top-left when current source is the Sora 2 video */}
              {aiVideo?.url && (
                <div
                  data-testid="ai-video-badge"
                  className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 bg-emerald-500/[0.12] border border-emerald-400/30 backdrop-blur-sm pointer-events-none"
                >
                  <Sparkles className="w-3 h-3 text-emerald-300" />
                  <span className="text-[10px] font-mono uppercase tracking-wider text-emerald-200">
                    {isTurkish ? 'Sora 2 ile üretildi' : 'Generated by Sora 2'}
                  </span>
                </div>
              )}

              {/* Admin generate trigger — top-right, only visible to admins */}
              {isAdmin && (
                <button
                  type="button"
                  onClick={generateAiVideo}
                  disabled={generating}
                  data-testid="admin-generate-explainer-btn"
                  className="absolute top-3 right-3 inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/95 hover:bg-white text-black text-[10px] font-mono uppercase tracking-wider disabled:opacity-60 disabled:cursor-wait transition-colors shadow-lg"
                  title={isTurkish ? 'Yeni AI tanıtım videosu üret (~3-5dk)' : 'Generate new AI explainer (~3-5min)'}
                >
                  {generating ? (
                    <>
                      <Loader2 className="w-3 h-3 animate-spin" />
                      {isTurkish ? 'Üretiliyor…' : 'Generating…'}
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-3 h-3" />
                      {aiVideo ? (isTurkish ? 'Yeniden üret' : 'Regenerate')
                              : (isTurkish ? 'AI ile üret' : 'Generate w/ AI')}
                    </>
                  )}
                </button>
              )}

              {/* Loading spinner */}
              {isLoading && !error && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/40 pointer-events-none">
                  <Loader2 className="w-10 h-10 text-white animate-spin" />
                </div>
              )}

              {/* Error overlay */}
              {error && (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/80 p-6 text-center" data-testid="video-error">
                  <AlertCircle className="w-10 h-10 text-red-400 mb-3" />
                  <p className="text-sm text-white/80 mb-4 max-w-md">{error}</p>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={retry}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-white text-black text-xs font-medium hover:bg-white/90 transition-colors"
                    >
                      <RotateCw className="w-3.5 h-3.5" />
                      {isTurkish ? 'Tekrar Dene' : 'Retry'}
                    </button>
                    <a
                      href={aiVideo?.url || FALLBACK_SRC}
                      download
                      className="inline-flex items-center gap-2 px-4 py-2 border border-white/20 text-white/80 text-xs hover:bg-white/5 transition-colors"
                    >
                      {isTurkish ? 'Videoyu İndir' : 'Download Video'}
                    </a>
                  </div>
                </div>
              )}

              {/* Play overlay (only when not playing and no error) */}
              {!isPlaying && !error && !isLoading && (
                <button
                  onClick={togglePlay}
                  className="absolute inset-0 flex items-center justify-center bg-black/40 hover:bg-black/30 transition-colors group/play"
                  aria-label={isTurkish ? 'Videoyu oynat' : 'Play video'}
                  data-testid="video-play-btn"
                >
                  <div className="relative">
                    {/* Pulse ring */}
                    <span className="absolute inset-0 rounded-full bg-white/20 animate-ping" />
                    <div className="relative w-20 h-20 bg-white rounded-full flex items-center justify-center group-hover/play:scale-110 transition-transform">
                      <Play className="w-7 h-7 text-black ml-1" fill="currentColor" />
                    </div>
                  </div>
                </button>
              )}

              {/* Mute/unmute when playing */}
              {isPlaying && !error && (
                <button
                  onClick={toggleMute}
                  className="absolute top-3 right-3 w-9 h-9 bg-black/60 hover:bg-black/80 border border-white/10 flex items-center justify-center text-white transition-colors z-10"
                  aria-label={muted ? (isTurkish ? 'Sesi aç' : 'Unmute') : (isTurkish ? 'Sesi kapat' : 'Mute')}
                  data-testid="video-mute-btn"
                >
                  {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                </button>
              )}
            </div>
          </div>

          {/* Caption strip */}
          <div className="mt-6 flex flex-wrap items-center justify-between gap-4 text-xs font-mono uppercase tracking-wider text-white/60">
            <div className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 bg-[#FF3B30] rounded-full animate-pulse" />
              {isTurkish ? 'Canlı Platform Görüntüsü' : 'Live Platform Footage'}
            </div>
            <div className="flex gap-6">
              <span>{isTurkish ? 'Sesli Komut' : 'Voice Command'}</span>
              <span>→</span>
              <span>2D {isTurkish ? 'Konsept' : 'Concept'}</span>
              <span>→</span>
              <span>3D {isTurkish ? 'Prototip' : 'Prototype'}</span>
              <span>→</span>
              <span>{isTurkish ? 'CAD Dışa Aktarımı' : 'CAD Export'}</span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
